import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  UserCheck, 
  Calendar, 
  TrendingUp, 
  AlertTriangle, 
  Activity,
  DollarSign,
  Brain,
  ArrowUpRight
} from 'lucide-react';
import { KPICard } from '../../components/shared/KPICard';
import { AIInsightPanel } from '../../components/shared/AIInsightCard';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Mock data
const kpiData = [
  { title: 'Active Patients', value: '2,847', change: '+12%', changeType: 'positive', icon: Users, description: 'Across all services' },
  { title: 'Active Professionals', value: '156', change: '+5%', changeType: 'positive', icon: UserCheck, description: 'Physio, OT, Speech, Diet, Care' },
  { title: 'Sessions Today', value: '342', change: '+8%', changeType: 'positive', icon: Calendar, description: '28 in progress' },
  { title: 'Compliance Rate', value: '78.4%', change: '+3.2%', changeType: 'positive', icon: TrendingUp },
  { title: 'Dropout Risk', value: '47', change: '-15%', changeType: 'positive', icon: AlertTriangle, description: 'Patients at risk' },
  { title: 'Outcome Score', value: '8.2', change: '+0.4', changeType: 'positive', icon: Activity, description: 'Avg improvement' },
];

const complianceTrend = [
  { month: 'Jan', compliance: 72, target: 80 },
  { month: 'Feb', compliance: 74, target: 80 },
  { month: 'Mar', compliance: 73, target: 80 },
  { month: 'Apr', compliance: 76, target: 80 },
  { month: 'May', compliance: 78, target: 80 },
  { month: 'Jun', compliance: 78.4, target: 80 },
];

const serviceMix = [
  { name: 'Physiotherapy', value: 45, color: 'hsl(var(--chart-1))' },
  { name: 'Occupational Therapy', value: 20, color: 'hsl(var(--chart-2))' },
  { name: 'Speech Therapy', value: 15, color: 'hsl(var(--chart-3))' },
  { name: 'Diet & Nutrition', value: 12, color: 'hsl(var(--chart-4))' },
  { name: 'Care & Companion', value: 8, color: 'hsl(var(--chart-5))' },
];

const revenueData = [
  { service: 'Physio', revenue: 245000, sessions: 1240 },
  { service: 'OT', revenue: 98000, sessions: 520 },
  { service: 'Speech', revenue: 76000, sessions: 380 },
  { service: 'Diet', revenue: 54000, sessions: 290 },
  { service: 'Care', revenue: 42000, sessions: 180 },
];

const aiInsights = [
  {
    title: '23 patients showing dropout signals',
    reason: 'Missed 2+ sessions in the past week with declining engagement scores.',
    confidence: 87,
    type: 'risk',
    actions: ['Notify therapists', 'Send reminders', 'Offer package']
  },
  {
    title: 'Compliance spike in Kuala Lumpur region',
    reason: 'New reminder system showing 15% improvement in session attendance.',
    confidence: 92,
    type: 'improvement',
    actions: ['Expand to other regions', 'View details']
  },
  {
    title: '12 patients ready for service escalation',
    reason: 'Recovery trajectory analysis suggests additional OT or Speech support.',
    confidence: 78,
    type: 'compliance',
    actions: ['Review patients', 'Notify care team']
  },
  {
    title: 'Therapist capacity warning: Penang',
    reason: 'Demand forecast shows 20% shortage in physiotherapy slots next week.',
    confidence: 84,
    type: 'risk',
    actions: ['View forecast', 'Activate standby']
  },
];

export default function AdminDashboard() {
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">AI Command Center</h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time overview of your healthcare operations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Last 30 days
          </Button>
          <Button size="sm">
            <Brain className="w-4 h-4 mr-2" />
            Run AI Analysis
          </Button>
        </div>
      </div>
      
      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpiData.map((kpi, idx) => (
          <KPICard key={idx} {...kpi} index={idx} />
        ))}
      </div>
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Charts Section */}
        <div className="xl:col-span-2 space-y-6">
          {/* Compliance Trend */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-display">Compliance Trend</CardTitle>
              <Tabs defaultValue="6m" className="w-auto">
                <TabsList className="h-8">
                  <TabsTrigger value="1m" className="text-xs h-6">1M</TabsTrigger>
                  <TabsTrigger value="3m" className="text-xs h-6">3M</TabsTrigger>
                  <TabsTrigger value="6m" className="text-xs h-6">6M</TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={complianceTrend}>
                    <defs>
                      <linearGradient id="complianceGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <YAxis domain={[60, 100]} tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="target" 
                      stroke="hsl(var(--muted-foreground))" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      dot={false}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="compliance" 
                      stroke="hsl(var(--primary))" 
                      strokeWidth={2}
                      fill="url(#complianceGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Revenue by Service */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base font-display">Revenue by Service</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs">
                View Report <ArrowUpRight className="w-3 h-3 ml-1" />
              </Button>
            </CardHeader>
            <CardContent>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(value) => `RM${value/1000}k`} />
                    <YAxis type="category" dataKey="service" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" width={50} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`RM ${value.toLocaleString()}`, 'Revenue']}
                    />
                    <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          
          {/* Service Mix */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-display">Service Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={serviceMix}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {serviceMix.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => [`${value}%`, 'Share']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {serviceMix.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-muted-foreground truncate">{item.name}</span>
                      <span className="font-medium text-foreground ml-auto">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-display">Regional Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { region: 'Kuala Lumpur', compliance: 82, patients: 1240 },
                    { region: 'Penang', compliance: 76, patients: 520 },
                    { region: 'Johor Bahru', compliance: 79, patients: 480 },
                    { region: 'Ipoh', compliance: 74, patients: 320 },
                    { region: 'Kuching', compliance: 71, patients: 287 },
                  ].map((item, idx) => (
                    <div key={idx} className="space-y-1.5">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-foreground font-medium">{item.region}</span>
                        <span className="text-muted-foreground">{item.compliance}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <motion.div 
                          initial={{ width: 0 }}
                          animate={{ width: `${item.compliance}%` }}
                          transition={{ duration: 0.5, delay: idx * 0.1 }}
                          className="h-full bg-primary rounded-full"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* AI Insights Panel */}
        <AIInsightPanel 
          title="AI Alerts & Recommendations" 
          insights={aiInsights}
          className="h-fit"
        />
      </div>
    </div>
  );
}
